(() => {
  if (window.DaziaDiscountOffer) return;

  const LEGACY_GIFT_PROPERTY = '_dazia_free_gift';
  const CLAIM_ATTRIBUTE = 'dazia_100off_claimed__';
  const DEFAULT_CONFIG = {
    thresholdCents: 100000,
    discountAmountCents: 10000,
    baseCurrency: 'USD',
    discountCode: '100OFF',
    deadline: '2026-10-01T03:59:59Z',
  };

  let syncInProgress = false;
  let countdownInterval;
  let cartUpdateUnsubscriber;
  let serverClockOffsetMs = 0;
  let expirySyncStarted = false;
  let staleCodeCleanupComplete = false;

  const getConfigElement = () => document.querySelector('[data-dazia-discount-offer-config]');

  const getConfig = () => {
    const element = getConfigElement();
    if (!element) return DEFAULT_CONFIG;
    return {
      thresholdCents: Number(element.dataset.thresholdCents) || DEFAULT_CONFIG.thresholdCents,
      discountAmountCents: Number(element.dataset.discountAmountCents) || DEFAULT_CONFIG.discountAmountCents,
      baseCurrency: element.dataset.baseCurrency || DEFAULT_CONFIG.baseCurrency,
      discountCode: element.dataset.discountCode || DEFAULT_CONFIG.discountCode,
      deadline: element.dataset.deadline || DEFAULT_CONFIG.deadline,
    };
  };

  const getDeadlineMs = (config) => Date.parse(config.deadline);
  const getCurrentTimeMs = () => Date.now() + serverClockOffsetMs;
  const isOfferExpired = (config) => {
    const deadlineMs = getDeadlineMs(config);
    return !Number.isFinite(deadlineMs) || getCurrentTimeMs() >= deadlineMs;
  };

  const updateServerClock = (response) => {
    const serverDate = response.headers.get('Date');
    if (!serverDate) return;
    const serverTimeMs = Date.parse(serverDate);
    if (Number.isFinite(serverTimeMs)) serverClockOffsetMs = serverTimeMs - Date.now();
  };

  const padTime = (value) => String(value).padStart(2, '0');

  const hideOffer = () => {
    document.querySelectorAll('[data-dazia-discount-offer]').forEach((offer) => {
      offer.hidden = true;
    });
  };

  const updateCountdown = () => {
    const config = getConfig();
    const deadlineMs = getDeadlineMs(config);
    const remainingMs = Number.isFinite(deadlineMs) ? Math.max(0, deadlineMs - getCurrentTimeMs()) : 0;
    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    document.querySelectorAll('[data-dazia-free-gift-countdown]').forEach((countdown) => {
      const values = {
        '[data-dazia-countdown-days]': days,
        '[data-dazia-countdown-hours]': hours,
        '[data-dazia-countdown-minutes]': minutes,
        '[data-dazia-countdown-seconds]': seconds,
      };
      Object.entries(values).forEach(([selector, value]) => {
        const element = countdown.querySelector(selector);
        if (element) element.textContent = padTime(value);
      });
      countdown.setAttribute(
        'aria-label',
        `Offer ends in ${days} ${days === 1 ? 'day' : 'days'}, ${hours} hours, ${minutes} minutes, and ${seconds} seconds`
      );
    });

    if (remainingMs > 0) {
      expirySyncStarted = false;
      return;
    }

    hideOffer();
    if (!expirySyncStarted && !syncInProgress) {
      expirySyncStarted = true;
      sync().catch((error) => console.error('[Dazia discount offer expiry]', error));
    }
  };

  const startCountdown = () => {
    window.clearInterval(countdownInterval);
    updateCountdown();
    countdownInterval = window.setInterval(updateCountdown, 1000);
  };

  const isLegacyGiftItem = (item) => String(item.properties?.[LEGACY_GIFT_PROPERTY]) === 'true';
  const getQualifyingSubtotal = (cart) =>
    cart.items.reduce(
      (subtotal, item) => subtotal + (isLegacyGiftItem(item) ? 0 : Number(item.original_line_price || 0)),
      0
    );
  const getVisibleItemCount = (cart) =>
    cart.items.reduce((count, item) => count + (isLegacyGiftItem(item) ? 0 : Number(item.quantity || 0)), 0);

  const getThreshold = (cart, config) => {
    if (cart.currency === config.baseCurrency) return config.thresholdCents;
    const rate = Number(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? Math.round(config.thresholdCents * rate) : config.thresholdCents;
  };

  const getAppliedDiscountCodes = (cart) => {
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...cart.items.flatMap((item) =>
        (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)
      ),
    ];
    return [...new Set(
      applications
        .filter((application) => application?.type === 'discount_code' && application.title)
        .map((application) => application.title)
    )];
  };

  const hasOfferCode = (cart, config) =>
    getAppliedDiscountCodes(cart).some((code) => code.toUpperCase() === config.discountCode.toUpperCase());

  const hasClaimMarker = (cart) => String(cart.attributes?.[CLAIM_ATTRIBUTE]) === 'true';

  const updateCartBubble = (cart) => {
    const count = getVisibleItemCount(cart);
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;
    if (count === 0) {
      bubble.remove();
      return;
    }
    const visibleCount = bubble.querySelector('span[aria-hidden="true"]') || bubble.querySelector('span');
    const accessibleCount = bubble.querySelector('.visually-hidden');
    if (visibleCount) visibleCount.textContent = count;
    if (accessibleCount) accessibleCount.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
  };

  const getSectionIds = () => {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    const mainItems = document.getElementById('main-cart-items')?.dataset.id;
    const mainFooter = document.getElementById('main-cart-footer')?.dataset.id;
    if (mainItems) ids.push(mainItems);
    if (mainFooter) ids.push(mainFooter);
    return [...new Set(ids)].slice(0, 5);
  };

  const getCartRoute = (routeName, fallbackPath) => {
    if (typeof routes !== 'undefined' && routes?.[routeName]) return routes[routeName];
    return `${window.Shopify?.routes?.root || '/'}${fallbackPath}`;
  };

  const replaceContents = (html, currentSelector, sourceSelector = currentSelector) => {
    if (!html) return;
    const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
    const current = document.querySelector(currentSelector);
    const source = sourceDocument.querySelector(sourceSelector);
    if (current && source) current.innerHTML = source.innerHTML;
  };

  const renderSections = (sections) => {
    if (!sections) return;
    replaceContents(sections['cart-drawer'], 'cart-drawer-items');
    replaceContents(sections['cart-drawer'], '.drawer__footer');
    replaceContents(sections['cart-icon-bubble'], '#cart-icon-bubble');
    const mainItemsId = document.getElementById('main-cart-items')?.dataset.id;
    if (mainItemsId && sections[mainItemsId]) {
      replaceContents(sections[mainItemsId], '#main-cart-items .js-contents');
    }
    const mainFooterId = document.getElementById('main-cart-footer')?.dataset.id;
    if (mainFooterId && sections[mainFooterId]) {
      replaceContents(sections[mainFooterId], '#main-cart-footer .js-contents');
    }
  };

  const cartRequest = async (url, payload, { render = true } = {}) => {
    const requestConfig = typeof fetchConfig === 'function'
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    const response = await fetch(url, {
      ...requestConfig,
      body: JSON.stringify({ ...payload, sections: getSectionIds(), sections_url: window.location.pathname }),
    });
    updateServerClock(response);
    const state = await response.json();
    if (!response.ok || state.status) throw new Error(state.description || 'Unable to update the cart.');
    if (render) renderSections(state.sections);
    return state;
  };

  const fetchCart = async () => {
    const cartJsonUrl = typeof routes !== 'undefined' && routes?.cart_url
      ? `${routes.cart_url}.js`
      : `${window.Shopify?.routes?.root || '/'}cart.js`;
    const response = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
    updateServerClock(response);
    if (!response.ok) throw new Error('Unable to read the cart.');
    return response.json();
  };

  const setApplyButtonLoading = (button, isLoading) => {
    if (!button) return;
    button.classList.toggle('is-loading', isLoading);
    button.disabled = isLoading;
    if (isLoading) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-label', 'Applying discount code');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-label');
    }
  };

  const updateOfferUI = (cart, config, errorMessage = '') => {
    const expired = isOfferExpired(config);
    const threshold = getThreshold(cart, config);
    const subtotal = getQualifyingSubtotal(cart);
    const eligible = subtotal >= threshold;
    const applied = hasOfferCode(cart, config);
    const claimed = applied && hasClaimMarker(cart);

    document.querySelectorAll('[data-dazia-discount-offer]').forEach((offer) => {
      const button = offer.querySelector('[data-dazia-discount-apply]');
      const label = button?.querySelector('[data-dazia-discount-button-label]');
      const status = offer.querySelector('[data-dazia-discount-status]');
      offer.hidden = expired || claimed;
      offer.classList.toggle('is-locked', !eligible);
      offer.classList.toggle('is-applied', claimed);
      if (button && !button.classList.contains('is-loading')) button.disabled = !eligible || claimed || expired;
      if (button) button.title = errorMessage;
      if (label) label.textContent = errorMessage ? 'Try again' : claimed ? 'Claimed' : 'Claim';
      if (status) {
        status.textContent = errorMessage || (claimed
          ? `Code ${config.discountCode} is applied. You saved $100.`
          : '');
      }
    });
  };

  const updateDiscountCodes = async (cart, codes, { claimed = false } = {}) => {
    const state = await cartRequest(
      getCartRoute('cart_update_url', 'cart/update.js'),
      {
        discount: codes.join(','),
        attributes: { [CLAIM_ATTRIBUTE]: claimed ? 'true' : '' },
      },
      { render: false }
    );
    return { state, cart: await fetchCart() };
  };

  const removeLegacyGiftItems = async (cart) => {
    const giftItems = cart.items.filter(isLegacyGiftItem);
    if (!giftItems.length) return { cart, state: null };
    const updates = Object.fromEntries(giftItems.map((item) => [item.key, 0]));
    const state = await cartRequest(
      getCartRoute('cart_update_url', 'cart/update.js'),
      { updates },
      { render: false }
    );
    return { state, cart: await fetchCart() };
  };

  const sync = async ({ forceApply = false, cartData = null } = {}) => {
    if (syncInProgress) return;
    const config = getConfig();
    if (!config?.thresholdCents || !config?.discountCode) return;
    syncInProgress = true;

    try {
      let cart = cartData?.currency && Array.isArray(cartData.items) ? cartData : await fetchCart();
      let stateToRender = null;

      const legacyCleanup = await removeLegacyGiftItems(cart);
      cart = legacyCleanup.cart;
      stateToRender = legacyCleanup.state || stateToRender;

      const expired = isOfferExpired(config);
      const eligible = getQualifyingSubtotal(cart) >= getThreshold(cart, config);
      const existingCodes = getAppliedDiscountCodes(cart);
      const offerCodeApplied = hasOfferCode(cart, config);
      const claimMarked = hasClaimMarker(cart);
      const remainingCodes = existingCodes.filter(
        (code) => code.toUpperCase() !== config.discountCode.toUpperCase()
      );

      if (expired || !eligible) {
        if (offerCodeApplied || claimMarked || !staleCodeCleanupComplete) {
          const removal = await updateDiscountCodes(cart, remainingCodes);
          cart = removal.cart;
          stateToRender = removal.state;
          staleCodeCleanupComplete = true;
        }
      } else if (forceApply) {
        const requestedCodes = [...new Set([...remainingCodes, config.discountCode])];
        const application = await updateDiscountCodes(cart, requestedCodes, { claimed: true });
        cart = application.cart;
        if (!hasOfferCode(cart, config) || !hasClaimMarker(cart)) {
          const cleanupCodes = getAppliedDiscountCodes(cart).filter(
            (code) => code.toUpperCase() !== config.discountCode.toUpperCase()
          );
          const cleanup = await updateDiscountCodes(cart, cleanupCodes);
          cart = cleanup.cart;
          renderSections(cleanup.state.sections);
          updateCartBubble(cart);
          updateOfferUI(cart, config, `Code ${config.discountCode} could not be applied. Check its Shopify discount settings.`);
          return;
        }
        stateToRender = application.state;
        staleCodeCleanupComplete = true;
      } else if (offerCodeApplied && !claimMarked) {
        const removal = await updateDiscountCodes(cart, remainingCodes);
        cart = removal.cart;
        stateToRender = removal.state;
        staleCodeCleanupComplete = true;
      } else if (claimMarked && !offerCodeApplied) {
        const cleanup = await updateDiscountCodes(cart, existingCodes);
        cart = cleanup.cart;
        stateToRender = cleanup.state;
        staleCodeCleanupComplete = true;
      } else if (!staleCodeCleanupComplete) {
        const cleanup = await updateDiscountCodes(cart, remainingCodes);
        cart = cleanup.cart;
        stateToRender = cleanup.state;
        staleCodeCleanupComplete = true;
      }

      if (stateToRender?.sections) renderSections(stateToRender.sections);
      updateCartBubble(cart);
      updateOfferUI(cart, config);
      if (expired) hideOffer();
    } catch (error) {
      console.error('[Dazia discount offer]', error);
      try {
        const cart = await fetchCart();
        updateOfferUI(cart, config, 'The discount could not be updated. Please refresh and try again.');
      } catch (cartError) {
        console.error('[Dazia cart refresh]', cartError);
      }
    } finally {
      syncInProgress = false;
    }
  };

  const applyOffer = async (button) => {
    const config = getConfig();
    if (isOfferExpired(config)) {
      hideOffer();
      return;
    }
    setApplyButtonLoading(button, true);
    try {
      await sync({ forceApply: true });
    } finally {
      if (button?.isConnected) setApplyButtonLoading(button, false);
    }
  };

  window.DaziaDiscountOffer = { apply: applyOffer, sync };

  document.addEventListener('click', (event) => {
    const applyButton = event.target.closest('[data-dazia-discount-apply]');
    if (!applyButton || applyButton.disabled) return;
    event.preventDefault();
    applyOffer(applyButton);
  });

  const initialize = () => {
    startCountdown();
    sync();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'dazia-discount-offer') return;
        setTimeout(() => sync({ cartData: event.cartData }), 0);
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) updateCountdown();
  });
  window.addEventListener('pagehide', () => {
    cartUpdateUnsubscriber?.();
    window.clearInterval(countdownInterval);
  }, { once: true });
})();
